import React from 'react'

import type { SpaceProps } from '../themes'

export const extractSpaceProps = <T extends SpaceProps>(
  props: T,
): [SpaceProps, Omit<T, keyof SpaceProps>] => {
  const {
    m,
    mt,
    mr,
    mb,
    ml,
    mx,
    my,
    p,
    pt,
    pr,
    pb,
    pl,
    px,
    py,
    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    marginX,
    marginY,
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    paddingX,
    paddingY,
    ...rest
  } = props

  const spaceProps: SpaceProps = {
    m,
    mt,
    mr,
    mb,
    ml,
    mx,
    my,
    p,
    pt,
    pr,
    pb,
    pl,
    px,
    py,
    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    marginX,
    marginY,
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    paddingX,
    paddingY,
  }

  const spacePropKeys = Object.keys(spaceProps) as (keyof SpaceProps)[]

  return [
    // Omit undefined values
    spacePropKeys.reduce((acc, key) => {
      if (spaceProps[key] === undefined) return acc
      return { ...acc, [key]: spaceProps[key] }
    }, {} as SpaceProps),
    rest,
  ]
}

/**
 * Extract space props (like mt, mx, py) from a set of props.
 */
export const useExtractSpaceProps = <T extends SpaceProps>(
  props: T,
): [SpaceProps, Omit<T, keyof SpaceProps>] => {
  const [spaceProps, rest] = React.useMemo(
    () => extractSpaceProps<T>(props),
    [props],
  )

  return [spaceProps, rest]
}
